/* eslint-disable no-process-env */
/**
 * CORS utility for Global Auth API routes
 */

export interface ICorsOptions {
  allowedOrigins: string[];
  maxAge?: string;
  allowedMethods?: string;
  allowCredentials?: boolean;
}

const DEFAULT_ALLOWED_METHODS = 'GET,POST,PATCH,PUT,DELETE,OPTIONS';
const DEFAULT_MAX_AGE = '86400';

/**
 * Gets the list of allowed origins based on the environment
 */
export function getAllowedOrigins(): string[] {
  return process.env.NEXT_PUBLIC_APP_ENV === 'production'
    ? ['republic.com', 'seedrs.com']
    : [
        'republic.com',
        'seedrs.com',
        'r-eng.app',
        'republic.local',
        'localhost',
      ];
}

/**
 * Checks if the origin is allowed based on the allowed origins list
 */
export function isOriginAllowed(
  host: string,
  allowedOrigins: string[]
): boolean {
  return (
    allowedOrigins.includes(host) ||
    allowedOrigins.some((allowedOrigin) => host.endsWith(`.${allowedOrigin}`))
  );
}

/**
 * Creates CORS headers for the given request
 * @param request - The incoming request
 * @param options - CORS configuration options
 * @returns Headers object with CORS headers set
 */
export function createCorsHeaders(
  request: Request,
  options?: Partial<ICorsOptions>
): Headers {
  const {
    allowedOrigins = getAllowedOrigins(),
    maxAge = DEFAULT_MAX_AGE,
    allowedMethods = DEFAULT_ALLOWED_METHODS,
    allowCredentials = true,
  } = options || {};

  const origin = request.headers.get('origin');
  const headers = new Headers();

  if (!origin) return headers;

  let host: string;
  try {
    host = new URL(origin).hostname.toLowerCase();
  } catch {
    return headers;
  }

  if (isOriginAllowed(host, allowedOrigins)) {
    headers.set('Access-Control-Allow-Origin', origin);
    headers.set('Access-Control-Max-Age', maxAge);
    headers.set('Access-Control-Allow-Methods', allowedMethods);
    headers.set(
      'Access-Control-Expose-Headers',
      'Content-Length,Content-Type,Set-Cookie,Auth-Proxied-For'
    );

    if (allowCredentials) {
      headers.set('Access-Control-Allow-Credentials', 'true');
    }

    const requestHeaders = request.headers.get(
      'Access-Control-Request-Headers'
    );
    if (requestHeaders) {
      headers.set('Access-Control-Allow-Headers', requestHeaders);
    }
  }

  return headers;
}

/**
 * Appends CORS headers to an existing Response
 * @param response - The response to add CORS headers to
 * @param corsHeaders - The CORS headers to append
 * @returns The response with CORS headers added
 */
export function appendCorsHeaders(
  response: Response,
  corsHeaders: Headers
): Response {
  corsHeaders.forEach((value, key) => {
    response.headers.set(key, value);
  });
  return response;
}
